import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
    Container,
    Box,
    Typography,
    TextField,
    Button,
    Alert,
    Link,
    Paper
} from '@mui/material';

const validationSchema = Yup.object({
    email: Yup.string()
        .email('Invalid email address')
        .required('Email is required'),
    password: Yup.string()
        .required('Password is required')
});

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, isAuthenticated, user } = useAuth();
    const [error, setError] = useState('');
    const [redirected, setRedirected] = useState(false);

    // Redirect to dashboard if already authenticated
    useEffect(() => {
        // Only redirect if user is authenticated and page hasn't already handled a redirect
        if (isAuthenticated && user && !redirected) {
            console.log('User already authenticated, redirecting to dashboard');
            setRedirected(true);
            // Get the intended destination or default to dashboard
            const from = location.state?.from?.pathname || '/dashboard';
            navigate(from, { replace: true });
        }
    }, [isAuthenticated, user, navigate, redirected, location]);

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            setError('');
            await login(values.email, values.password);
            
            // Mark as redirected to avoid redirect loop
            setRedirected(true);
            
            // Get the intended destination or default to dashboard
            const from = location.state?.from?.pathname || '/dashboard';
            
            // Use replace: true to avoid adding to history stack
            navigate(from, { replace: true });
        } catch (err) {
            console.error('Login error:', err);
            
            // Extract error message based on response structure
            let errorMessage = 'Login failed. Please check your email and password.';
            
            if (err.response?.data) {
                // Get the specific error message from the server
                const { error, errorType, details } = err.response.data;
                
                if (error) {
                    errorMessage = error;
                }
                
                // Handle specific error types
                if (errorType === 'validation') {
                    // For validation errors, provide a more user-friendly message
                    if (details && details.includes('subscription')) {
                        errorMessage = 'Your account has subscription validation issues. Our team has been notified and will fix this shortly.';
                    }
                }
            } else if (err.message && err.message.includes('Network Error')) {
                errorMessage = 'Unable to connect to the server. Please check your internet connection.';
            }
            
            setError(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Container component="main" maxWidth="sm">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}
            >
                <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
                    <Typography component="h1" variant="h5" align="center" gutterBottom>
                        Welcome Back to ReaderFlex
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Formik
                        initialValues={{
                            email: '',
                            password: ''
                        }}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                            <Form>
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    name="email"
                                    label="Email"
                                    type="email"
                                    value={values.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.email && Boolean(errors.email)}
                                    helperText={touched.email && errors.email}
                                />

                                <TextField
                                    fullWidth
                                    margin="normal"
                                    name="password"
                                    label="Password"
                                    type="password"
                                    value={values.password}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.password && Boolean(errors.password)}
                                    helperText={touched.password && errors.password}
                                />

                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    sx={{ mt: 3, mb: 2 }}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Signing in...' : 'Sign In'}
                                </Button>

                                <Box sx={{ textAlign: 'center' }}>
                                    <Link href="/register" variant="body2">
                                        Don't have an account? Sign up
                                    </Link>
                                    <Box mt={1}>
                                        <Link href="/forgot-password" variant="body2">
                                            Forgot password?
                                        </Link>
                                    </Box>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Paper>
            </Box>
        </Container>
    );
};

export default Login;
